import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, filter, firstValueFrom, fromEvent, shareReplay } from 'rxjs';
import { getHiddenIframeRefreshService } from './hiddenIframeRefresh';

export interface RefreshTokenTemplateDTO {
  token: string;
  exp: string;
}

export interface TokenTemplateDTO {
  token: string;
  roles?: string[];
  tenant_id?: string;
  exp?: string;
}

export interface TokensDTO {
  access_token: TokenTemplateDTO;
  refresh_token: RefreshTokenTemplateDTO;
}

export interface DarwinJwtPayload {
  roles?: string[];
  tenant_id?: string;
  exp: number;
  iat: number;
  // Add other JWT payload properties as needed
}

class AuthTokensService {
  private authDomain: string;
  private el: HTMLIFrameElement;
  private iframeLoadEvent$: any;
  private credentials$ = new BehaviorSubject<TokensDTO | null>(null);
  private iframePostEvent$: any;
  private rolesFromTokenSubject = new BehaviorSubject<string[] | null>(null);
  private authTenantIdSubject = new BehaviorSubject<string>('');
  private tokenMonitorTimer: NodeJS.Timeout | null = null;
  private darwinLoginUrl: string | null = null;

  constructor(authDomain: string, darwinLoginUrl?: string) {
    this.authDomain = authDomain;
    this.darwinLoginUrl = darwinLoginUrl || null;
    this.el = document.createElement('iframe');

    if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
      console.warn('DEV AUTH BYPASS ACTIVE -- using mock credentials, skipping iframe auth');
      this._injectDevCredentials();
      return;
    }

    this.iframeLoadEvent$ = fromEvent(this.el, 'load').pipe(shareReplay());
    this.iframePostEvent$ = fromEvent<MessageEvent>(window, 'message').pipe(
      filter(
        (event) =>
          event.origin.includes('localhost') ||
          event.origin.includes('darwingov.com')
      )
    );

    this.listenForTokens();
    this.addAuthIframe();
  }

  private _injectDevCredentials(): void {
    const mockCredentials: TokensDTO = {
      access_token: {
        token: 'dev-bypass-mock-token',
        roles: [
          'bidchecker_viewer',
          'bidchecker_admin',
          'marketplace_viewer',
          'admin',
          'manager',
          'evaluator',
          'auditor',
          'viewer',
        ],
        tenant_id: 'dev-tenant-000000000000',
        exp: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      },
      refresh_token: {
        token: 'dev-bypass-mock-refresh-token',
        exp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
    };
    this.credentials$.next(mockCredentials);
    this.rolesFromTokenSubject.next(mockCredentials.access_token.roles || null);
    this.authTenantIdSubject.next(mockCredentials.access_token.tenant_id || '');
  }

  get rolesFromToken$() {
    return this.rolesFromTokenSubject.asObservable();
  }

  get authTenantId$() {
    return this.authTenantIdSubject.asObservable();
  }

  get credentials() {
    return this.credentials$.asObservable();
  }

  async getTokens(): Promise<TokensDTO | null> {
    return firstValueFrom(this.credentials$);
  }

  async getCredentials(): Promise<TokensDTO> {
    return new Promise((resolve) => {
      this.credentials$.subscribe((res) => {
        if (!res) {
          return;
        }
        resolve(res);
      });
    });
  }

  private addAuthIframe() {
    this.iframeLoadEvent$.subscribe();
    const displayDiv = document.body;
    this.el.style.display = 'none';
    this.el.src = this.authDomain;
    displayDiv.appendChild(this.el);
  }

  private listenForTokens() {
    this.iframePostEvent$.subscribe((res: MessageEvent) => {
      let data = res.data;

      if (typeof data === 'string' && (data.startsWith('{') || data.startsWith('['))) {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      const creds = data?.credentials || (data?.access_token ? data : null);
      const isNullAuth = data?.credentials === null || data?.access_token === null;

      if (creds?.access_token) {
        this.rolesFromTokenSubject.next(creds.access_token.roles || null);
        this.authTenantIdSubject.next(creds.access_token.tenant_id || '');
        this.credentials$.next(creds);
      } else if (isNullAuth) {
        this.credentials$.next(null);
      }
    });
  }

  private syncTokensToIframe(credentials: TokensDTO | null): Promise<void> {
    return new Promise((resolve) => {
      const message = JSON.stringify({ credentials });
      const post = () => {
        this.el.contentWindow?.postMessage(message, '*');
        resolve();
      };
      if (this.el.contentWindow) {
        post();
      } else {
        this.iframeLoadEvent$.subscribe(post);
      }
    });
  }

  setCredentials(credentials: TokensDTO): Promise<void> {
    console.log('🔄 AuthTokensService: setCredentials called with exp:', credentials?.access_token?.exp);
    this.credentials$.next(credentials);

    if (credentials?.access_token?.exp) {
      this.startTokenMonitoring();
    } else {
      console.warn('❌ AuthTokensService: No exp field found in credentials, monitoring not started');
    }

    return this.syncTokensToIframe(credentials);
  }

  getRefreshToken(): string | null {
    return this.credentials$.value?.refresh_token?.token || null;
  }

  setAccessToken(token: TokenTemplateDTO): Promise<void> {
    return new Promise((resolve) => {
      const credentials = {
        access_token: token,
        refresh_token: {
          token: this.credentials$.value?.refresh_token?.token || '',
          exp: this.credentials$.value?.refresh_token?.exp || '',
        } as RefreshTokenTemplateDTO,
      };
      
      setTimeout(async () => {
        await this.setCredentials(credentials);
        return resolve();
      });
    });
  }

  clearTokens(): Promise<void> {
    this.credentials$.next(null);
    this.stopTokenMonitoring();
    return this.syncTokensToIframe(null);
  }

  async decodedToken() {
    const credentials = await firstValueFrom(this.credentials$);
    return credentials?.access_token;
  }

  decodeToken(token: string) {
    return jwtDecode<DarwinJwtPayload>(token);
  }

  getCurrentCredentials(): TokensDTO | null {
    return this.credentials$.value;
  }

  forceUpdateCredentials(credentials: TokensDTO): void {
    console.log('🔄 AuthTokensService: forceUpdateCredentials called');
    this.credentials$.next(credentials);
    this.rolesFromTokenSubject.next(credentials?.access_token?.roles || null);
    this.authTenantIdSubject.next(credentials?.access_token?.tenant_id || '');
    void this.syncTokensToIframe(credentials);
  }

  /**
   * Check if the access token is expiring within the next 5 minutes
   */
  private isTokenExpiring(): boolean {
    const credentials = this.credentials$.value;
    if (!credentials?.access_token?.exp) {
      console.log('🔍 isTokenExpiring: No exp field found');
      return false;
    }
    
    const expTime = new Date(credentials.access_token.exp);
    const now = new Date();
    const timeUntilExpiry = expTime.getTime() - now.getTime();
    
    console.log('🔍 isTokenExpiring check:', {
      exp: credentials.access_token.exp,
      expTime: expTime.toISOString(),
      now: now.toISOString(),
      timeUntilExpiryMs: timeUntilExpiry,
      timeUntilExpiryMin: Math.round(timeUntilExpiry / 60000),
      isExpiring: timeUntilExpiry <= 300000 && timeUntilExpiry > 0
    });
    
    // Refresh if expiring within 5 minutes (300000ms)
    return timeUntilExpiry <= 300000 && timeUntilExpiry > 0;
  }

  /**
   * Start monitoring token expiration and refresh proactively
   */
  private startTokenMonitoring(): void {
    this.stopTokenMonitoring(); // Clear any existing timer
    
    console.log('🔄 AuthTokensService: Starting token expiration monitoring');
    
    this.tokenMonitorTimer = setInterval(() => {
      if (this.isTokenExpiring()) {
        console.log('🔄 AuthTokensService: Token expiring soon, triggering proactive refresh');
        this.refreshTokenProactively();
      }
    }, 60000); // Check every minute
  }

  /**
   * Stop token monitoring
   */
  private stopTokenMonitoring(): void {
    if (this.tokenMonitorTimer) {
      clearInterval(this.tokenMonitorTimer);
      this.tokenMonitorTimer = null;
      console.log('🔄 AuthTokensService: Stopped token expiration monitoring');
    }
  }

  /**
   * Refresh token proactively using hidden iframe service
   */
  private async refreshTokenProactively(): Promise<void> {
    if (!this.darwinLoginUrl) {
      console.warn('🔄 AuthTokensService: Cannot refresh proactively - no Darwin login URL configured');
      return;
    }

    try {
      console.log('🔄 AuthTokensService: Starting proactive token refresh');
      
      const hiddenRefreshService = getHiddenIframeRefreshService(this.authDomain, this.darwinLoginUrl);
      const refreshResult = await hiddenRefreshService.refreshTokens();
      
      if (refreshResult.success && refreshResult.access_token) {
        console.log('✅ AuthTokensService: Proactive token refresh successful');
        
        // Parse JWT to extract roles and tenant_id
        let roles: string[] = [];
        let tenant_id = '';
        let exp = '';
        
        try {
          const decodedToken = this.decodeToken(refreshResult.access_token);
          roles = decodedToken.roles || [];
          tenant_id = decodedToken.tenant_id || '';
          exp = new Date(decodedToken.exp * 1000).toISOString(); // Convert Unix timestamp to ISO string
        } catch (error) {
          console.warn('Failed to decode JWT token during proactive refresh:', error);
          // Use existing values as fallback
          const currentCreds = this.credentials$.value;
          roles = currentCreds?.access_token?.roles || [];
          tenant_id = currentCreds?.access_token?.tenant_id || '';
        }
        
        // Update credentials with new tokens
        const newCredentials = {
          access_token: {
            token: refreshResult.access_token,
            roles: roles,
            tenant_id: tenant_id,
            exp: exp
          },
          refresh_token: {
            token: refreshResult.refresh_token || this.getRefreshToken() || '',
            exp: this.credentials$.value?.refresh_token?.exp || ''
          }
        };
        
        this.forceUpdateCredentials(newCredentials);
        console.log('✅ AuthTokensService: Token credentials updated after proactive refresh');
      } else {
        console.error('❌ AuthTokensService: Proactive token refresh failed:', refreshResult.error);
      }
    } catch (error) {
      console.error('❌ AuthTokensService: Proactive token refresh error:', error);
      // Let 401 interceptor handle it as fallback
    }
  }
}

// Singleton instance
let authTokensServiceInstance: AuthTokensService | null = null;

export const createAuthTokensService = (authDomain: string, darwinLoginUrl?: string): AuthTokensService => {
  if (!authTokensServiceInstance) {
    authTokensServiceInstance = new AuthTokensService(authDomain, darwinLoginUrl);
  }
  return authTokensServiceInstance;
};

export const getAuthTokensService = (): AuthTokensService => {
  if (!authTokensServiceInstance) {
    throw new Error('AuthTokensService not initialized. Call createAuthTokensService first.');
  }
  return authTokensServiceInstance;
};